import { useCallback, useMemo } from 'react'

import { useToast } from '@/components/ui/toast.tsx'
import type { TaskEditorValues } from '@/modules/tasks/components/create-task-dialog/schema'
import type { SchedulerEditorValues } from '@/modules/tasks/components/task-scheduler-dialog/schema.ts'
import {
  useCreateRepeatableTask,
  useUpdateRepeatableTask,
} from '@/modules/tasks/query/repeatable-task-query'
import {
  useCreateTask,
  useUpdateTask,
  useUpdateTaskList,
} from '@/modules/tasks/query/task-query'
import { weekdays } from '@/modules/tasks/service/repeatable-task-service.ts'
import { handleError } from '@/utils/error'

const Result = {
  Ok: <T>(value?: T) => ['Ok', value] as const,
  Error: (error?: unknown) => ['Error', error] as const,
}

function toRate(values: SchedulerEditorValues) {
  if (!values.period) {
    return []
  }
  switch (values.period) {
    case 'Daily':
      return []
    case 'Weekly':
      return weekdays.map((weekday) =>
        values.rateDaysOfWeek?.includes(weekday) ? 1 : 0,
      )
    case 'Monthly':
      return [values.rateDayOfMonth ?? 0]
    case 'Annually':
      return [values.rateDayOfMonth ?? 0, values.rateMonth ?? 0]
  }
}

export function useSaveRepeatable() {
  const create = useCreateRepeatableTask()
  const update = useUpdateRepeatableTask()
  const toast = useToast()

  const mutate = useCallback(
    (values: SchedulerEditorValues) => {
      const { id } = values

      if (!id) {
        return create
          .mutateAsync({
            ...values,
            rate: toRate(values),
          })
          .then((created) => {
            toast.success('Recurring Task created successfully.')

            return Result.Ok(created)
          })
          .catch((error) => {
            handleError(error, 'Failed to create recurring task')

            return Result.Error(error)
          })
      }

      return update
        .mutateAsync({ ...values, rate: toRate(values), id })
        .then((updated) => {
          toast.success('Recurring Task updated successfully.')

          return Result.Ok(updated)
        })
        .catch((error) => {
          handleError(error, 'Failed to update recurring task')

          return Result.Error(error)
        })
    },
    [create, toast, update],
  )

  return useMemo(
    () => ({
      mutate,
      error: create.error ?? update.error,
      isLoading: create.isLoading || update.isLoading,
    }),
    [mutate, create.error, create.isLoading, update.error, update.isLoading],
  )
}

export function useSaveRegularTask() {
  const create = useCreateTask()
  const update = useUpdateTask()
  const updateTaskList = useUpdateTaskList()
  const toast = useToast()

  const mutateTaskList = useCallback(
    (id: number, taskListId: number) => {
      return updateTaskList.mutateAsync({ id, newListId: taskListId })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [useUpdateTaskList],
  )

  const mutate = useCallback(
    (values: TaskEditorValues) => {
      const { id } = values

      if (!id) {
        return create
          .mutateAsync(values)
          .then(async (created) => {
            if (values.taskList?.id) {
              await mutateTaskList(created.id, values.taskList.id)
            }
            toast.success('Task created successfully.')

            return Result.Ok(created)
          })
          .catch((error) => {
            handleError(error, 'Failed to create task')

            return Result.Error(error)
          })
      }

      return update
        .mutateAsync({ ...values, id })
        .then(async (updated) => {
          if (values.taskList?.id) {
            await mutateTaskList(updated.id, values.taskList.id)
          }
          toast.success('Task updated successfully.')

          return Result.Ok(updated)
        })
        .catch((error) => {
          handleError(error, 'Failed to update task')

          return Result.Error(error)
        })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [create, toast, update],
  )

  return useMemo(
    () => ({
      mutate,
      error: create.error ?? update.error,
      isLoading: create.isLoading || update.isLoading,
    }),
    [mutate, create.error, create.isLoading, update.error, update.isLoading],
  )
}
